import React, { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowRight, School, BarChart3, Bell, MessageSquare, Users, TrendingUp, Shield, Clock, FileText, AlertCircle, CheckCircle, Eye, Calendar, Award, Zap, Gift, ChefHat, Building2 } from 'lucide-react';
import HeaderEN from '../components/HeaderEN';
import '../styles/index.css';

const PageSchoolsEN = () => {
  const navigate = useNavigate();
  useEffect(() => { document.title = "EcoLunch - Schools | Pilot Cockpit"; }, []);

  return (
    <div className="home-container">
      <HeaderEN currentPage="schools" lang="en" />

      {/* Hero Schools */}
      <section className="hero-section">
        <div className="hero-background">
          <div className="hero-gradient"></div>
          <div className="hero-grid"></div>
        </div>
        <div className="hero-content">
          <div className="hero-badge animate-fade-in-up">
            <School size={16} />
            <span>Schools Dashboard</span>
          </div>
          <h1 className="hero-title animate-fade-in-up" style={{ animationDelay: '0.1s' }}>
            <span style={{ color: 'var(--actor-schools)' }}>Schools</span>
          </h1>
          <p className="hero-subtitle animate-fade-in-up" style={{ animationDelay: '0.2s', fontSize: '1.3rem', lineHeight: '1.8' }}>
            <strong style={{ color: 'var(--actor-schools)' }}>100%</strong> real-time visibility<br />
            Automated reporting • Simplified communication
          </p>
          <div className="hero-stats animate-fade-in-up" style={{ animationDelay: '0.4s' }}>
            <div className="stat-item">
              <div className="stat-number">100%</div>
              <div className="stat-label">Real-time visibility</div>
            </div>
            <div className="stat-divider"></div>
            <div className="stat-item">
              <div className="stat-number">0</div>
              <div className="stat-label">Manual entry</div>
            </div>
            <div className="stat-divider"></div>
            <div className="stat-item">
              <div className="stat-number">∞</div>
              <div className="stat-label">Automated insights</div>
            </div>
          </div>
        </div>
      </section>

      {/* Manifesto — Why EcoLunch for Schools */}
      <section className="section" data-testid="manifeste-schools">
        <div className="watermark-pattern"></div>
        <div className="section-container">
          <div className="section-header">
            <h2 className="section-title">Why EcoLunch for Schools?</h2>
          </div>
          <div className="manifeste-tile" data-testid="manifeste-gratuit-schools">
            <div className="manifeste-tile-header">
              <div className="portal-icon" style={{color: '#3B82F6'}}><Gift size={30} /></div>
              <h3>Free for schools. Free for parents. Sustainable for the ecosystem.</h3>
            </div>
            <p>EcoLunch will be and remain free for schools. Better yet, our model also allows donation contributions, so a school can not only offer a structured, modern and frictionless service, but also derive a positive benefit from it.</p>
            <p>For parents, platform usage is also free. The goal is clear: make access to the service <strong>simple, smooth, centralized and without unnecessary barriers</strong>.</p>
          </div>
        </div>
      </section>

      {/* School Features */}
      <section className="section problem-section">
        <div className="watermark-pattern lighter"></div>
        <div className="section-container">
          <div className="section-header">
            <h2 className="section-title">School Features</h2>
            <p className="section-subtitle">All tools to efficiently manage your school food service</p>
          </div>
          <div className="benefits-grid-4col">
            <div className="benefit-card">
              <div className="benefit-icon"><Eye size={32} /></div>
              <h3>Real-time visibility</h3>
              <p>See exactly how many students have ordered, who eats what, and food trends instantly.</p>
            </div>
            <div className="benefit-card">
              <div className="benefit-icon"><BarChart3 size={32} /></div>
              <h3>Automated reporting</h3>
              <p>Participation, satisfaction, and compliance reports generated automatically. No more Excel files.</p>
            </div>
            <div className="benefit-card">
              <div className="benefit-icon"><MessageSquare size={32} /></div>
              <h3>Centralized communication</h3>
              <p>Direct channel with caterers, parents, and management. Everything in one place, trackable and documented.</p>
            </div>
            <div className="benefit-card">
              <div className="benefit-icon"><AlertCircle size={32} /></div>
              <h3>Incident management</h3>
              <p>Integrated ticket system to quickly manage problems: missing meals, allergies, quality.</p>
            </div>
            <div className="benefit-card">
              <div className="benefit-icon"><Clock size={32} /></div>
              <h3>Reduced management time</h3>
              <p>Eliminate time-consuming manual management. EcoLunch centralizes and automates everything to save you hours every week.</p>
            </div>
            <div className="benefit-card">
              <div className="benefit-icon"><TrendingUp size={32} /></div>
              <h3>Continuous improvement</h3>
              <p>Identify trends and improvement opportunities thanks to real-time data and automatic recommendations.</p>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section id="features" className="section features-section">
        <div className="watermark-pattern"></div>
        <div className="section-container">
          <div className="section-header">
            <h2 className="section-title">Dashboard Features</h2>
            <p className="section-subtitle">
              All tools to manage efficiently
            </p>
          </div>

          <div className="benefits-grid">
            <div className="benefit-card">
              <div className="benefit-icon"><BarChart3 size={32} /></div>
              <h3>Real-time dashboard</h3>
              <p>Instant overview: participation rate, today's orders, weekly trends. Everything updated live.</p>
            </div>

            <div className="benefit-card">
              <div className="benefit-icon"><Users size={32} /></div>
              <h3>Per-student tracking</h3>
              <p>Order history for each student. Detect changes in eating behavior quickly.</p>
            </div>

            <div className="benefit-card">
              <div className="benefit-icon"><TrendingUp size={32} /></div>
              <h3>Satisfaction analytics</h3>
              <p>Student ratings and comments aggregated. Identify popular menus and improvement points.</p>
            </div>

            <div className="benefit-card">
              <div className="benefit-icon"><Calendar size={32} /></div>
              <h3>Menu planning</h3>
              <p>View upcoming menus. Collaborate with caterer to adjust according to student preferences.</p>
            </div>

            <div className="benefit-card">
              <div className="benefit-icon"><FileText size={32} /></div>
              <h3>Automatic reports</h3>
              <p>Monthly reports generated automatically: participation, revenue, satisfaction. Exportable PDF/Excel.</p>
            </div>

            <div className="benefit-card">
              <div className="benefit-icon"><Bell size={32} /></div>
              <h3>Smart alerts</h3>
              <p>Notifications in case of participation drop, quality problem, or incident. React quickly.</p>
            </div>

            <div className="benefit-card">
              <div className="benefit-icon"><MessageSquare size={32} /></div>
              <h3>Multi-actor communication</h3>
              <p>Integrated messaging with caterers, parents, and management. Complete exchange history.</p>
            </div>

            <div className="benefit-card">
              <div className="benefit-icon"><Shield size={32} /></div>
              <h3>Allergy management</h3>
              <p>Complete list of students with dietary restrictions. Automatic alerts in case of risk.</p>
            </div>

            <div className="benefit-card">
              <div className="benefit-icon"><Award size={32} /></div>
              <h3>Compliance tracking</h3>
              <p>Ensure compliance with nutritional standards. Compliance reports generated automatically.</p>
            </div>

            <div className="benefit-card">
              <div className="benefit-icon"><FileText size={32} /></div>
              <h3>Export and reports</h3>
              <p>Export your data in CSV/PDF and create custom reports for your specific needs.</p>
            </div>
          </div>
        </div>
      </section>

      {/* Communication Section */}
      <section className="section coparent-section">
        <div className="watermark-pattern lighter"></div>
        <div className="section-container">
          <div className="section-header">
            <h2 className="section-title">Simplified communication</h2>
            <p className="section-subtitle">
              A central hub for all your communications
            </p>
          </div>

          <div className="benefits-grid-4col">
            <div className="benefit-card">
              <h4><ChefHat size={16} style={{display:'inline', verticalAlign:'middle', color:'#84CC16'}} /> Caterer Channel</h4>
              <p>Direct communication with your caterer for menu adjustments, quality problems, or volume changes.</p>
            </div>

            <div className="benefit-card">
              <h4><Users size={16} style={{display:'inline', verticalAlign:'middle', color:'#84CC16'}} /> Parents Channel</h4>
              <p>Answer parent questions, share menu announcements, and manage special requests.</p>
            </div>

            <div className="benefit-card">
              <h4><Building2 size={16} style={{display:'inline', verticalAlign:'middle', color:'#84CC16'}} /> Management Channel</h4>
              <p>Escalate important information to your school board management.</p>
            </div>

            <div className="benefit-card">
              <h4><FileText size={16} style={{display:'inline', verticalAlign:'middle', color:'#84CC16'}} /> Ticket system</h4>
              <p>Manage incidents (missing meals, quality problems) with a structured and trackable ticket system.</p>
            </div>
          </div>
        </div>
      </section>

      {/* EcoPulse CC Mini-Card */}
      <section className="section" data-testid="ecopulse-mini-schools">
        <div className="watermark-pattern"></div>
        <div className="section-container">
          <div className="ecopulse-mini-card" style={{borderColor: 'rgba(34, 197, 94, 0.4)'}}>
            <div className="ecopulse-mini-header">
              <div className="portal-icon" style={{color: '#84CC16'}}>
                <Zap size={28} />
              </div>
              <div>
                <h3>EcoPulse CC™ <span style={{fontSize: '0.85rem', fontWeight: 500, opacity: 0.7}}>for Schools</span></h3>
                <p style={{fontSize: '0.9rem', color: 'var(--color-text-secondary)', margin: 0}}>
                  One sentence is all you need
                </p>
              </div>
            </div>
            <div className="ecopulse-mini-commands">
              <div className="ecopulse-mini-cmd">How many students ordered today?</div>
              <div className="ecopulse-mini-cmd">3rd grade participation rate</div>
              <div className="ecopulse-mini-cmd">Students allergic to peanuts</div>
              <div className="ecopulse-mini-cmd">Attendance report for council</div>
            </div>
            <button onClick={() => navigate('/en/ecopulse')} className="ecopulse-mini-link">
              Discover all EcoPulse commands <ArrowRight size={16} />
            </button>
          </div>
        </div>
      </section>

      {/* Site Web */}
      <section className="section" style={{paddingTop: '2rem', paddingBottom: '2rem'}}>
        <div className="section-container" style={{textAlign: 'center'}}>
          <a href="https://ecotechsysteme.ca" target="_blank" rel="noopener noreferrer" style={{color: 'var(--color-lime)', fontSize: '1.2rem', fontWeight: 600, textDecoration: 'none', borderBottom: '2px solid rgba(132,204,22,0.3)', paddingBottom: '0.25rem'}}>
            ecotechsysteme.ca
          </a>
        </div>
      </section>

      {/* Footer */}
      <footer className="footer">
        <div className="footer-content">
          <div className="footer-main">
            <div className="footer-brand">
              <div className="footer-logo">
                <img src="/logo-ecolunch.png" alt="EcoLunch" className="footer-logo-image" />
              </div>
              <p className="footer-tagline">
                The unified platform to modernize school meals in Quebec
              </p>
            </div>

            <div className="footer-links">
              <div className="footer-column">
                <h4>Solutions</h4>
                <a onClick={() => navigate('/en/caterers')}>For Caterers</a>
                <a onClick={() => navigate('/en/parents')}>For Parents</a>
                <a onClick={() => navigate('/en/schools')}>For Schools</a>
                <a onClick={() => navigate('/en/directions')}>For Boards</a>
                <a onClick={() => navigate('/en/css')}>For CSS</a>
              </div>

              <div className="footer-column">
                <h4>Platform</h4>
                <a onClick={() => navigate('/en/platform')}>Architecture</a>
                <a onClick={() => navigate('/en/coparenting')}>Co-Parenting</a>
                <a onClick={() => navigate('/en/ecopulse')}>EcoPulse CC</a>
                <a onClick={() => navigate('/en/ecoloop')}>EcoLoop</a>
              </div>
            </div>
          </div>

          <div className="footer-bottom">
            <div className="footer-legal">
              <span>© 2025 EcoLunch. All rights reserved.</span>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default PageSchoolsEN;
